import api from '../utils/api'

// ============================================================================
// Translation Configs (Providers) API
// ============================================================================

export const getConfigs = async () => {
  const response = await api.get('/admin/configs/')
  return response.data
}

export const getConfig = async (configId) => {
  const response = await api.get(`/admin/configs/${configId}`)
  return response.data
}

export const createConfig = async (data) => {
  const response = await api.post('/admin/configs/', data)
  return response.data
}

export const updateConfig = async (configId, data) => {
  const response = await api.put(`/admin/configs/${configId}`, data)
  return response.data
}

export const deleteConfig = async (configId) => {
  await api.delete(`/admin/configs/${configId}`)
}

// ============================================================================
// Translation Accounts (Models) API
// ============================================================================

export const getAccounts = async (configId) => {
  const response = await api.get(`/admin/configs/${configId}/accounts/`)
  return response.data
}

export const createAccount = async (configId, data) => {
  const response = await api.post(`/admin/configs/${configId}/accounts/`, data)
  return response.data
}

export const updateAccount = async (accountId, data) => {
  const response = await api.put(`/admin/configs/accounts/${accountId}`, data)
  return response.data
}

export const deleteAccount = async (accountId) => {
  await api.delete(`/admin/configs/accounts/${accountId}`)
}

// ============================================================================
// LLM Instructions API
// ============================================================================

export const getInstructions = async () => {
  const response = await api.get('/admin/instructions/')
  return response.data
}

export const getInstruction = async (instructionId) => {
  const response = await api.get(`/admin/instructions/${instructionId}`)
  return response.data
}

export const createInstruction = async (data) => {
  const response = await api.post('/admin/instructions/', data)
  return response.data
}

export const updateInstruction = async (instructionId, data) => {
  const response = await api.put(`/admin/instructions/${instructionId}`, data)
  return response.data
}

export const deleteInstruction = async (instructionId) => {
  await api.delete(`/admin/instructions/${instructionId}`)
}

// ============================================================================
// Supported Languages API
// ============================================================================

export const getLanguages = async (provider = null) => {
  const params = provider ? { provider } : {}
  const response = await api.get('/admin/languages/languages', { params })
  return response.data
}

export const bulkCreateLanguages = async (provider, languages) => {
  const response = await api.post('/admin/languages/bulk', { provider, languages })
  return response.data
}

export const deleteLanguage = async (languageId) => {
  await api.delete(`/admin/languages/${languageId}`)
}

export const deleteLanguages = async (provider, locale = null, name = null) => {
  const params = { provider }
  if (locale) params.locale = locale
  if (name) params.name = name
  await api.delete('/admin/languages', { params })
}

// ============================================================================
// Quality Matrix API
// ============================================================================

export const getQualityMatrix = async () => {
  const response = await api.get('/admin/quality/matrix')
  return response.data
}

export const getQualityStatus = async () => {
  const response = await api.get('/admin/quality/status')
  return response.data
}

export const getDomainCoverage = async () => {
  const response = await api.get('/admin/quality/domain-coverage')
  return response.data
}

export const evaluateQuality = async (data) => {
  // Quality evaluation can take several minutes, use extended timeout (10 minutes)
  const response = await api.post('/admin/quality/evaluate', data, {
    timeout: 600000  // 10 minutes in milliseconds
  })
  return response.data
}

export const getQualityRecommendations = async (data) => {
  const response = await api.post('/admin/quality/recommendations', data)
  return response.data
}

export const getAccountQuality = async (accountId, languagePair = null, domain = null) => {
  const params = {}
  if (languagePair) params.language_pair = languagePair
  if (domain) params.domain = domain
  const response = await api.get(`/admin/quality/accounts/${accountId}/quality`, { params })
  return response.data
}

export const resetAccountQuality = async (accountId, languagePair = null, domain = null) => {
  const params = {}
  if (languagePair) params.language_pair = languagePair
  if (domain) params.domain = domain
  const response = await api.delete(`/admin/quality/accounts/${accountId}/quality`, { params })
  return response.data
}

// ============================================================================
// Translation Test API
// ============================================================================

export const testTranslation = async (data) => {
  const response = await api.post('/admin/translation-test/translate', data)
  return response.data
}

export const testReview = async (data) => {
  const response = await api.post('/admin/translation-test/review', data)
  return response.data
}

// ============================================================================
// Benchmark Data API
// ============================================================================

export const getBenchmarkCoverage = async () => {
  const response = await api.get('/admin/benchmarks/coverage')
  return response.data
}

export const getBenchmarkSamples = async (domain = null, languagePair = null, isActive = null, limit = 100, offset = 0, datasetId = null, sourceOrigin = null) => {
  const params = { limit, offset }
  if (domain) params.domain = domain
  if (languagePair) params.language_pair = languagePair
  if (isActive !== null) params.is_active = isActive
  if (datasetId !== null) params.dataset_id = datasetId
  if (sourceOrigin) params.source_origin = sourceOrigin
  const response = await api.get('/admin/benchmarks/samples', { params })
  return response.data
}

export const getBenchmarkSample = async (sampleId) => {
  const response = await api.get(`/admin/benchmarks/samples/${sampleId}`)
  return response.data
}

export const createBenchmarkSample = async (data) => {
  const response = await api.post('/admin/benchmarks/samples', data)
  return response.data
}

export const updateBenchmarkSample = async (sampleId, data) => {
  const response = await api.put(`/admin/benchmarks/samples/${sampleId}`, data)
  return response.data
}

export const deleteBenchmarkSample = async (sampleId) => {
  await api.delete(`/admin/benchmarks/samples/${sampleId}`)
}

export const bulkDeleteBenchmarkSamples = async (sampleIds) => {
  const response = await api.post('/admin/benchmarks/samples/bulk-delete', { sample_ids: sampleIds })
  return response.data
}

export const bulkReclassifyBenchmarkSamples = async (sampleIds, newDomain) => {
  const response = await api.post('/admin/benchmarks/samples/bulk-reclassify', {
    sample_ids: sampleIds,
    new_domain: newDomain
  })
  return response.data
}

export const importDefaultBenchmarks = async (data = {}) => {
  const response = await api.post('/admin/benchmarks/import-defaults', data)
  return response.data
}

export const getBenchmarkDomains = async () => {
  const response = await api.get('/admin/benchmarks/domains')
  return response.data
}

export const getBenchmarkLanguagePairs = async () => {
  const response = await api.get('/admin/benchmarks/language-pairs')
  return response.data
}

export const getBenchmarkDomainMappings = async () => {
  const response = await api.get('/admin/benchmarks/domain-mappings')
  return response.data
}

export const updateBenchmarkDomainMappings = async (mappings) => {
  const response = await api.put('/admin/benchmarks/domain-mappings', { mappings })
  return response.data
}

export const getBenchmarkLanguagePairSampleCounts = async (sourceLang = null, targetLang = null, datasetId = null) => {
  const params = {}
  if (sourceLang) params.source_lang = sourceLang
  if (targetLang) params.target_lang = targetLang
  if (datasetId !== null) params.dataset_id = datasetId
  const response = await api.get('/admin/benchmarks/language-pairs/sample-counts', { params })
  return response.data
}

// ============================================================================
// Benchmark Runs API
// ============================================================================

export const createBenchmarkRun = async (data) => {
  const response = await api.post('/admin/benchmarks/runs', data)
  return response.data
}

export const getBenchmarkRuns = async ({ accountId, sourceLang, targetLang, status, limit = 50, offset = 0 } = {}) => {
  const params = { limit, offset }
  if (accountId) params.account_id = accountId
  if (sourceLang) params.source_lang = sourceLang
  if (targetLang) params.target_lang = targetLang
  if (status) params.run_status = status
  const response = await api.get('/admin/benchmarks/runs', { params })
  return response.data
}

export const getBenchmarkRun = async (runId) => {
  const response = await api.get(`/admin/benchmarks/runs/${runId}`)
  return response.data
}

export const getBenchmarkRunResults = async (runId, { limit = 100, offset = 0 } = {}) => {
  const response = await api.get(`/admin/benchmarks/runs/${runId}/results`, {
    params: { limit, offset }
  })
  return response.data
}

export const deleteBenchmarkRun = async (runId) => {
  await api.delete(`/admin/benchmarks/runs/${runId}`)
}

export const getBenchmarkScores = async ({ accountIds, sourceLang, targetLang } = {}) => {
  const params = {}
  if (accountIds) params.account_ids = accountIds
  if (sourceLang) params.source_lang = sourceLang
  if (targetLang) params.target_lang = targetLang
  const response = await api.get('/admin/benchmarks/scores', { params })
  return response.data
}

export const compareBenchmarkAccounts = async ({ accountIds, sourceLang, targetLang, domain } = {}) => {
  const params = {
    account_ids: accountIds,
    source_lang: sourceLang,
    target_lang: targetLang
  }
  if (domain) params.domain = domain
  const response = await api.get('/admin/benchmarks/comparison', { params })
  return response.data
}

// ============================================================================
// Benchmark Versions API
// ============================================================================

export const getBenchmarkVersions = async ({ includeDeprecated = false } = {}) => {
  const response = await api.get('/admin/benchmarks/versions', {
    params: { include_deprecated: includeDeprecated }
  })
  return response.data
}

export const getActiveBenchmarkVersion = async () => {
  const response = await api.get('/admin/benchmarks/versions/active')
  return response.data
}

export const getBenchmarkVersion = async (versionId) => {
  const response = await api.get(`/admin/benchmarks/versions/${versionId}`)
  return response.data
}

export const createBenchmarkVersion = async (data) => {
  const response = await api.post('/admin/benchmarks/versions', data)
  return response.data
}

export const updateBenchmarkVersion = async (versionId, data) => {
  const response = await api.put(`/admin/benchmarks/versions/${versionId}`, data)
  return response.data
}

export const activateBenchmarkVersion = async (versionId) => {
  const response = await api.post(`/admin/benchmarks/versions/${versionId}/activate`)
  return response.data
}

export const deprecateBenchmarkVersion = async (versionId) => {
  const response = await api.post(`/admin/benchmarks/versions/${versionId}/deprecate`)
  return response.data
}

// ============================================================================
// Benchmark Datasets API
// ============================================================================

export const getBenchmarkDatasets = async () => {
  const response = await api.get('/admin/benchmarks/datasets')
  return response.data
}

export const getBenchmarkDataset = async (datasetName) => {
  const response = await api.get(`/admin/benchmarks/datasets/${datasetName}`)
  return response.data
}

export const getBenchmarkDatasetLanguages = async (datasetName) => {
  const response = await api.get(`/admin/benchmarks/datasets/${datasetName}/languages`)
  return response.data
}

export const syncBenchmarkDataset = async (data) => {
  // Use longer timeout (10 minutes) for dataset sync as large datasets take time to download
  const response = await api.post('/admin/benchmarks/datasets/sync', data, {
    timeout: 600000  // 10 minutes
  })
  return response.data
}

// ============================================================================
// Benchmark Scheduling API
// ============================================================================

export const getBenchmarkSchedules = async ({ accountId, isActive } = {}) => {
  const params = {}
  if (accountId) params.account_id = accountId
  if (isActive !== undefined) params.is_active = isActive
  const response = await api.get('/admin/benchmarks/schedules', { params })
  return response.data
}

export const getBenchmarkSchedule = async (scheduleId) => {
  const response = await api.get(`/admin/benchmarks/schedules/${scheduleId}`)
  return response.data
}

export const createBenchmarkSchedule = async (data) => {
  const response = await api.post('/admin/benchmarks/schedules', data)
  return response.data
}

export const updateBenchmarkSchedule = async (scheduleId, data) => {
  const response = await api.put(`/admin/benchmarks/schedules/${scheduleId}`, data)
  return response.data
}

export const deleteBenchmarkSchedule = async (scheduleId) => {
  await api.delete(`/admin/benchmarks/schedules/${scheduleId}`)
}

export const triggerBenchmarkSchedule = async (scheduleId) => {
  const response = await api.post(`/admin/benchmarks/schedules/${scheduleId}/trigger`)
  return response.data
}

export const pauseBenchmarkSchedule = async (scheduleId) => {
  const response = await api.post(`/admin/benchmarks/schedules/${scheduleId}/pause`)
  return response.data
}

export const resumeBenchmarkSchedule = async (scheduleId) => {
  const response = await api.post(`/admin/benchmarks/schedules/${scheduleId}/resume`)
  return response.data
}

// ============================================================================
// User Management API
// ============================================================================

export const getUsers = async (limit = 100, offset = 0, search = null) => {
  const params = { limit, offset }
  if (search) params.search = search
  const response = await api.get('/admin/users', { params })
  return response.data
}

export const fixSubscriptions = async () => {
  const response = await api.post('/admin/fix-subscriptions')
  return response.data
}

export const getUserSubscriptionStatus = async (userId) => {
  const response = await api.get(`/admin/subscription-status/${userId}`)
  return response.data
}

export const syncUserSubscription = async (userId) => {
  const response = await api.post(`/admin/sync-user-subscription/${userId}`)
  return response.data
}

// ============================================================================
// Admin Statistics API
// ============================================================================

/**
 * Get provider statistics with account breakdown
 * @param {Object} params - Query parameters
 * @param {string} params.dateFrom - Start date (YYYY-MM-DD)
 * @param {string} params.dateTo - End date (YYYY-MM-DD)
 * @param {number} params.days - Default days if no dates specified
 */
export const getProviderStats = async ({ dateFrom, dateTo, days = 30 } = {}) => {
  const params = {}
  if (dateFrom) params.date_from = dateFrom
  if (dateTo) params.date_to = dateTo
  if (!dateFrom && !dateTo) params.days = days
  const response = await api.get('/admin/stats/providers', { params })
  return response.data
}

/**
 * Get provider trends (time series data)
 * @param {Object} params - Query parameters
 * @param {string} params.dateFrom - Start date (YYYY-MM-DD)
 * @param {string} params.dateTo - End date (YYYY-MM-DD)
 * @param {number} params.days - Default days if no dates specified
 * @param {string} params.groupBy - 'day' or 'week'
 */
export const getProviderTrends = async ({ dateFrom, dateTo, days = 30, groupBy = 'day' } = {}) => {
  const params = { group_by: groupBy }
  if (dateFrom) params.date_from = dateFrom
  if (dateTo) params.date_to = dateTo
  if (!dateFrom && !dateTo) params.days = days
  const response = await api.get('/admin/stats/providers/trends', { params })
  return response.data
}

/**
 * Get overview dashboard KPIs
 * @param {Object} params - Query parameters
 * @param {string} params.dateFrom - Start date (YYYY-MM-DD)
 * @param {string} params.dateTo - End date (YYYY-MM-DD)
 * @param {number} params.days - Default days if no dates specified
 */
export const getOverviewStats = async ({ dateFrom, dateTo, days = 30 } = {}) => {
  const params = {}
  if (dateFrom) params.date_from = dateFrom
  if (dateTo) params.date_to = dateTo
  if (!dateFrom && !dateTo) params.days = days
  const response = await api.get('/admin/stats/overview', { params })
  return response.data
}

/**
 * Get overview trends (time series data)
 * @param {Object} params - Query parameters
 * @param {string} params.dateFrom - Start date (YYYY-MM-DD)
 * @param {string} params.dateTo - End date (YYYY-MM-DD)
 * @param {number} params.days - Default days if no dates specified
 * @param {string} params.groupBy - 'day' or 'week'
 */
export const getOverviewTrends = async ({ dateFrom, dateTo, days = 30, groupBy = 'day' } = {}) => {
  const params = { group_by: groupBy }
  if (dateFrom) params.date_from = dateFrom
  if (dateTo) params.date_to = dateTo
  if (!dateFrom && !dateTo) params.days = days
  const response = await api.get('/admin/stats/overview/trends', { params })
  return response.data
}

/**
 * Get user statistics
 * @param {Object} params - Query parameters
 * @param {string} params.dateFrom - Start date (YYYY-MM-DD)
 * @param {string} params.dateTo - End date (YYYY-MM-DD)
 * @param {number} params.days - Default days if no dates specified
 * @param {string} params.sortBy - Sort field
 * @param {string} params.order - 'asc' or 'desc'
 * @param {number} params.limit - Number of results
 * @param {number} params.offset - Pagination offset
 * @param {string} params.search - Search by email
 */
export const getUserStats = async ({
  dateFrom,
  dateTo,
  days = 30,
  sortBy = 'characters_used',
  order = 'desc',
  limit = 50,
  offset = 0,
  search = null
} = {}) => {
  const params = { sort_by: sortBy, order, limit, offset }
  if (dateFrom) params.date_from = dateFrom
  if (dateTo) params.date_to = dateTo
  if (!dateFrom && !dateTo) params.days = days
  if (search) params.search = search
  const response = await api.get('/admin/stats/users', { params })
  return response.data
}

/**
 * Export provider statistics as CSV
 * @param {Object} params - Query parameters
 * @param {string} params.dateFrom - Start date (YYYY-MM-DD)
 * @param {string} params.dateTo - End date (YYYY-MM-DD)
 * @param {number} params.days - Default days if no dates specified
 */
export const exportProviderStatsCSV = async ({ dateFrom, dateTo, days = 30 } = {}) => {
  const params = {}
  if (dateFrom) params.date_from = dateFrom
  if (dateTo) params.date_to = dateTo
  if (!dateFrom && !dateTo) params.days = days
  const response = await api.get('/admin/stats/export/providers', {
    params,
    responseType: 'blob'
  })
  return response
}

/**
 * Export user statistics as CSV
 * @param {Object} params - Query parameters
 * @param {string} params.dateFrom - Start date (YYYY-MM-DD)
 * @param {string} params.dateTo - End date (YYYY-MM-DD)
 * @param {number} params.days - Default days if no dates specified
 */
export const exportUserStatsCSV = async ({ dateFrom, dateTo, days = 30 } = {}) => {
  const params = {}
  if (dateFrom) params.date_from = dateFrom
  if (dateTo) params.date_to = dateTo
  if (!dateFrom && !dateTo) params.days = days
  const response = await api.get('/admin/stats/export/users', {
    params,
    responseType: 'blob'
  })
  return response
}

/**
 * Get language pair statistics per provider
 * @param {Object} params - Query parameters
 * @param {string} params.dateFrom - Start date (YYYY-MM-DD)
 * @param {string} params.dateTo - End date (YYYY-MM-DD)
 * @param {number} params.days - Default days if no dates specified
 * @param {string} params.provider - Filter by provider
 * @param {string} params.sourceLang - Filter by source language
 * @param {string} params.sortBy - Sort field
 * @param {string} params.order - 'asc' or 'desc'
 * @param {number} params.limit - Number of results
 * @param {number} params.offset - Pagination offset
 */
export const getLanguagePairStats = async ({
  dateFrom,
  dateTo,
  days = 30,
  provider = null,
  sourceLang = null,
  sortBy = 'characters',
  order = 'desc',
  limit = 100,
  offset = 0
} = {}) => {
  const params = { sort_by: sortBy, order, limit, offset }
  if (dateFrom) params.date_from = dateFrom
  if (dateTo) params.date_to = dateTo
  if (!dateFrom && !dateTo) params.days = days
  if (provider) params.provider = provider
  if (sourceLang) params.source_lang = sourceLang
  const response = await api.get('/admin/stats/language-pairs', { params })
  return response.data
}

/**
 * Export language pair statistics as CSV
 * @param {Object} params - Query parameters
 * @param {string} params.dateFrom - Start date (YYYY-MM-DD)
 * @param {string} params.dateTo - End date (YYYY-MM-DD)
 * @param {number} params.days - Default days if no dates specified
 * @param {string} params.provider - Filter by provider
 */
export const exportLanguagePairStatsCSV = async ({ dateFrom, dateTo, days = 30, provider = null } = {}) => {
  const params = {}
  if (dateFrom) params.date_from = dateFrom
  if (dateTo) params.date_to = dateTo
  if (!dateFrom && !dateTo) params.days = days
  if (provider) params.provider = provider
  const response = await api.get('/admin/stats/export/language-pairs', {
    params,
    responseType: 'blob'
  })
  return response
}

export default {
  // Configs
  getConfigs,
  getConfig,
  createConfig,
  updateConfig,
  deleteConfig,
  // Accounts
  getAccounts,
  createAccount,
  updateAccount,
  deleteAccount,
  // Instructions
  getInstructions,
  getInstruction,
  createInstruction,
  updateInstruction,
  deleteInstruction,
  // Languages
  getLanguages,
  bulkCreateLanguages,
  deleteLanguage,
  deleteLanguages,
  // Quality
  getQualityMatrix,
  getQualityStatus,
  getDomainCoverage,
  evaluateQuality,
  getQualityRecommendations,
  getAccountQuality,
  resetAccountQuality,
  // Translation Test
  testTranslation,
  testReview,
  // Benchmarks (Samples)
  getBenchmarkCoverage,
  getBenchmarkSamples,
  getBenchmarkSample,
  createBenchmarkSample,
  updateBenchmarkSample,
  deleteBenchmarkSample,
  bulkDeleteBenchmarkSamples,
  bulkReclassifyBenchmarkSamples,
  importDefaultBenchmarks,
  getBenchmarkDomains,
  getBenchmarkLanguagePairs,
  // Benchmark Runs
  createBenchmarkRun,
  getBenchmarkRuns,
  getBenchmarkRun,
  getBenchmarkRunResults,
  deleteBenchmarkRun,
  getBenchmarkScores,
  compareBenchmarkAccounts,
  // Benchmark Versions
  getBenchmarkVersions,
  getActiveBenchmarkVersion,
  getBenchmarkVersion,
  createBenchmarkVersion,
  updateBenchmarkVersion,
  activateBenchmarkVersion,
  deprecateBenchmarkVersion,
  // Benchmark Datasets
  getBenchmarkDatasets,
  getBenchmarkDataset,
  getBenchmarkDatasetLanguages,
  syncBenchmarkDataset,
  // Benchmark Scheduling
  getBenchmarkSchedules,
  getBenchmarkSchedule,
  createBenchmarkSchedule,
  updateBenchmarkSchedule,
  deleteBenchmarkSchedule,
  triggerBenchmarkSchedule,
  pauseBenchmarkSchedule,
  resumeBenchmarkSchedule,
  // User Management
  getUsers,
  fixSubscriptions,
  getUserSubscriptionStatus,
  syncUserSubscription,
  // Statistics
  getProviderStats,
  getProviderTrends,
  getOverviewStats,
  getOverviewTrends,
  getUserStats,
  exportProviderStatsCSV,
  exportUserStatsCSV,
  getLanguagePairStats,
  exportLanguagePairStatsCSV,
}
